import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "./CreateConsultationModal.scss";

import Button from "../../../atoms/Button/Button";
import Spinner from "../../../atoms/Spinner/Spinner";
import MonthCalendar, {
  toDateValue,
} from "../../../molecules/PatientDetailsV2/MonthCalendar/MonthCalendar";
import API from "../../../services/rest/api";
import useApi from "../../../hooks/useApi";
import { RootState } from "../../../store/store";
import {
  buildEntitlementProbePayload,
  ConsultationEntitlement,
  DuplicateConsultationHint,
  extractDuplicateConsultationHint,
  extractEntitlement,
  formatDuplicateConsultationWindow,
  formatEntitlementNumber,
  formatMonthlyCap,
  formatProgramCap,
  getEntitlementSummaryLine,
  hasEntitlementWarning,
} from "../../../utils/consultationEntitlement";

import { ReactComponent as CloseIcon } from "../../../assets/icons/patient-layout-v2/modal/CloseIcon.svg";
import { ReactComponent as InfoIcon } from "../../../assets/icons/patient-layout-v2/consults/InfoIcon.svg";
import { ReactComponent as LockIcon } from "../../../assets/icons/patient-layout-v2/consults/LockIcon.svg";
import { ReactComponent as MoonIcon } from "../../../assets/icons/patient-layout-v2/consults/MoonIcon.svg";
import { ReactComponent as SunIcon } from "../../../assets/icons/patient-layout-v2/consults/SunIcon.svg";
import { ReactComponent as SunriseIcon } from "../../../assets/icons/patient-layout-v2/consults/SunriseIcon.svg";
import { ReactComponent as SunsetIcon } from "../../../assets/icons/patient-layout-v2/consults/SunsetIcon.svg";

// Buckets and boundaries match the Angular calendar's booking modal, so a slot
// lands in the same part of the day on both surfaces.
const SLOT_GROUPS = [
  {
    key: "morning",
    label: "Morning",
    from: "00:00:00",
    to: "12:00:00",
    Icon: SunriseIcon,
  },
  {
    key: "afternoon",
    label: "Afternoon",
    from: "12:00:00",
    to: "16:00:00",
    Icon: SunIcon,
  },
  {
    key: "evening",
    label: "Evening",
    from: "16:00:00",
    to: "20:00:00",
    Icon: SunsetIcon,
  },
  {
    key: "night",
    label: "Night",
    from: "20:00:00",
    to: "24:00:01",
    Icon: MoonIcon,
  },
];

interface Slot {
  start_time: string;
  end_time: string;
  // Whether the slot can still be booked. Reserved is a separate axis: a
  // reserved slot is bookable too, it is just held for new patients.
  avail?: boolean;
  is_reserved?: boolean | string;
  health_coach_slot_time_rel_id?: string;
}

const isReserved = (slot: Slot) =>
  slot?.is_reserved === true || slot?.is_reserved === "Y";

const isTaken = (slot: Slot) => slot?.avail === false;

const slotKey = (slot: Slot) =>
  slot?.health_coach_slot_time_rel_id ||
  `${slot?.start_time}-${slot?.end_time}`;

// The API answers with a bare array on a good day and a keyed object on others,
// sometimes with the slots nested a level down. Flatten all three shapes.
const readSlots = (data: any): Slot[] => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.slots)) return data.slots;

  const nested: Slot[] = [];
  Object.values(data).forEach((value: any) => {
    if (!Array.isArray(value)) return;
    value.forEach((entry: any) => {
      if (entry?.start_time) nested.push(entry);
      else if (Array.isArray(entry?.slots)) nested.push(...entry.slots);
    });
  });
  return nested;
};

// "14:30:00" -> "2:30 PM"
const toDisplayTime = (time: string) => {
  const [rawHours, minutes] = (time || "").split(":");
  const hours = Number(rawHours);
  if (Number.isNaN(hours)) return time;

  const suffix = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes} ${suffix}`;
};

// Both ends of the slot, so the coach sees how long the consultation runs.
const toDisplayRange = (slot: Slot) =>
  slot.end_time
    ? `${toDisplayTime(slot.start_time)} - ${toDisplayTime(slot.end_time)}`
    : toDisplayTime(slot.start_time);

interface CreateConsultationModalProps {
  onClose: () => void;
  onBooked: () => void;
}

const CreateConsultationModal: React.FC<CreateConsultationModalProps> = ({
  onClose,
  onBooked,
}) => {
  const { patientDetails } = useSelector((state: RootState) => state.patient);
  const { healthCoachDetails } = useSelector(
    (state: RootState) => state.healthCoach,
  );
  const patient = patientDetails?.data;
  const coach = healthCoachDetails?.data;

  const [selectedDate, setSelectedDate] = useState(() =>
    toDateValue(new Date()),
  );
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [note, setNote] = useState("");
  const [slots, setSlots] = useState<Slot[]>([]);
  // Set when the API refuses the date outright — an expired plan, say — which
  // is a different thing from a day with nothing free.
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [entitlement, setEntitlement] =
    useState<ConsultationEntitlement | null>(null);
  const [duplicateHint, setDuplicateHint] =
    useState<DuplicateConsultationHint | null>(null);

  const {
    mutate: fetchSlots,
    isLoading: isLoadingSlots,
    response: slotsResponse,
    error: slotsError,
  } = useApi(API.GetAvailableSlots);

  const {
    mutate: fetchEntitlement,
    isLoading: isLoadingEntitlement,
    response: entitlementResponse,
    error: entitlementError,
  } = useApi(API.GetAppointmentTask);

  const {
    mutate: bookAppointment,
    isLoading: isBooking,
    response: bookingResponse,
    error: bookingError,
  } = useApi(API.BookAppointment);

  useEffect(() => {
    setSelectedSlot(null);
    setBlockedMessage(null);

    fetchSlots({
      availability_date: selectedDate,
      health_coach_id: coach?.health_coach_id,
      patient_id: patient?.patient_id,
    });

    if (coach?.health_coach_id && patient?.patient_id) {
      fetchEntitlement(
        buildEntitlementProbePayload({
          patientId: patient.patient_id,
          healthCoachId: coach.health_coach_id,
          // Midday probe, matching the Angular modal: the limits are per day,
          // not per slot, so any in-day time answers the same question.
          appointmentStart: `${selectedDate} 12:00:00`,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, coach?.health_coach_id, patient?.patient_id]);

  useEffect(() => {
    if (!slotsResponse && !slotsError) return;

    if (Number(slotsResponse?.code || 0) === 1) {
      setSlots(readSlots(slotsResponse.data));
      return;
    }

    setSlots([]);
    // code 0 carries a reason worth showing; anything else is just "none free".
    const response = slotsResponse || slotsError;
    setBlockedMessage(
      Number(response?.code) === 0 ? response?.message || null : null,
    );
  }, [slotsResponse, slotsError]);

  useEffect(() => {
    if (!entitlementResponse && !entitlementError) return;

    if (Number(entitlementResponse?.code || 0) === 1) {
      setEntitlement(extractEntitlement(entitlementResponse));
      setDuplicateHint(extractDuplicateConsultationHint(entitlementResponse));
      return;
    }

    setEntitlement(null);
    setDuplicateHint(null);
  }, [entitlementResponse, entitlementError]);

  useEffect(() => {
    if (!bookingResponse && !bookingError) return;

    if (Number(bookingResponse?.code || 0) === 1) {
      toast.success(bookingResponse?.message || "Consultation booked");
      onBooked();
      onClose();
      return;
    }

    toast.error(
      bookingResponse?.message ||
        bookingError?.message ||
        "Could not book this slot. Please try again.",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingResponse, bookingError]);

  // Reserved slots are held for new patients and get their own section, exactly
  // as the calendar's modal lays them out; the rest fall into time-of-day
  // buckets. Taken slots stay on screen greyed out — seeing that 2 PM is gone
  // tells the coach more than the slot quietly disappearing.
  const reserved = useMemo(() => slots.filter(isReserved), [slots]);

  const grouped = useMemo(
    () =>
      SLOT_GROUPS.map((group) => ({
        ...group,
        slots: slots.filter(
          (slot) =>
            !isReserved(slot) &&
            slot.start_time >= group.from &&
            slot.start_time < group.to,
        ),
      })).filter((group) => group.slots.length),
    [slots],
  );

  const hasSlots = !!reserved.length || !!grouped.length;

  // The patient's program has lapsed, so the caps no longer describe anything.
  const programExpired = hasEntitlementWarning(
    entitlement,
    "NO_ACTIVE_PROGRAM",
  );
  const limitReached = entitlement?.allowed === false;
  const naIfExpired = (value: string) => (programExpired ? "N/A" : value);
  const duplicateWindow = formatDuplicateConsultationWindow(duplicateHint);

  const renderSlot = (slot: Slot) => {
    const taken = isTaken(slot);
    const key = slotKey(slot);
    return (
      <button
        key={key}
        type="button"
        className={`cc-slot ${selectedSlot && slotKey(selectedSlot) === key ? "active" : ""}`}
        disabled={taken}
        title={taken ? "Already booked" : undefined}
        onClick={() => setSelectedSlot(slot)}
      >
        {isReserved(slot) && <span className="cc-slot-badge">Reserved</span>}
        <span className="cc-slot-time">{toDisplayRange(slot)}</span>
      </button>
    );
  };

  const handleBook = () => {
    if (!selectedSlot) {
      toast.error("Please pick a time slot first.");
      return;
    }

    bookAppointment({
      patient_id: patient?.patient_id,
      appointment_date: selectedDate,
      appointment_data: [
        {
          start_time: `${selectedDate} ${selectedSlot.start_time}`,
          end_time: `${selectedDate} ${selectedSlot.end_time}`,
        },
      ],
      note: note,
      type: "A",
    });
  };

  const patientName = patient?.name || "Patient";
  const patientPhone = patient?.contact_no
    ? `${patient?.country_code || ""} ${patient.contact_no}`.trim()
    : null;

  return createPortal(
    <div className="cc-overlay" onClick={onClose}>
      <div className="cc-modal" onClick={(event) => event.stopPropagation()}>
        <div className="cc-head">
          <div className="cc-head-meta">
            <h2 className="cc-title">New consultation</h2>
          </div>
          <button className="cc-close" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div className="cc-cols">
          <div className="cc-col cc-col-left">
            <div className="cc-field">
              <div className="cc-label">Patient</div>
              {/* Read-only for now: the portal already knows who is being
                  booked. Kept as a real input so it can become a patient
                  search later without rebuilding the row. */}
              <div className="cc-patient">
                <input
                  className="cc-patient-input"
                  value={patientName}
                  readOnly
                />
                {patientPhone && (
                  <span className="cc-patient-phone">{patientPhone}</span>
                )}
              </div>
            </div>

            <div className="cc-field">
              <div className="cc-label">Description</div>
              <textarea
                className="cc-note"
                value={note}
                placeholder="Add description"
                onChange={(event) => setNote(event.target.value)}
              />
            </div>

            <div className="cc-calendar-wrap">
              <MonthCalendar value={selectedDate} onChange={setSelectedDate} />
            </div>
          </div>

          <div className="cc-col cc-col-right">
            <div
              className={`cc-limits ${limitReached ? "blocked" : ""} ${
                isLoadingEntitlement ? "loading" : ""
              }`}
            >
              <div className="cc-limits-head">
                <InfoIcon />
                <span className="cc-limits-title">Consultation limits</span>
              </div>

              <div className="cc-badges">
                {programExpired && (
                  <span className="cc-badge expired">Program expired</span>
                )}
                {!programExpired && limitReached && (
                  <span className="cc-badge reached">Limit reached</span>
                )}
                {!programExpired && !limitReached && entitlement && (
                  <span className="cc-badge ok">Within limits</span>
                )}
                {duplicateHint && (
                  <span className="cc-badge duplicate">
                    Pending consultation
                  </span>
                )}
              </div>

              {!!getEntitlementSummaryLine(entitlement) && (
                <p className="cc-limits-summary">
                  {getEntitlementSummaryLine(entitlement)}
                </p>
              )}

              <dl className="cc-limits-grid">
                <div className="cc-limits-row">
                  <dt>Monthly limit</dt>
                  <dd>{naIfExpired(formatMonthlyCap(entitlement))}</dd>
                </div>
                <div className="cc-limits-row">
                  <dt>Program limit</dt>
                  <dd>{naIfExpired(formatProgramCap(entitlement))}</dd>
                </div>
                <div className="cc-limits-row">
                  <dt>Consumed this month</dt>
                  <dd>
                    {naIfExpired(
                      formatEntitlementNumber(
                        entitlement?.displayTotals?.totalConsumingMonth,
                      ),
                    )}
                  </dd>
                </div>
                <div className="cc-limits-row">
                  <dt>Consumed in program</dt>
                  <dd>
                    {naIfExpired(
                      formatEntitlementNumber(
                        entitlement?.displayTotals?.totalConsumingProgram,
                      ),
                    )}
                  </dd>
                </div>
                {!!duplicateWindow && (
                  <div className="cc-limits-row">
                    <dt>Pending consultation</dt>
                    <dd>{duplicateWindow}</dd>
                  </div>
                )}
              </dl>
            </div>

            <div className="cc-slots">
              <div className="cc-label">Available slots</div>

              {isLoadingSlots && (
                <div className="cc-slots-loading">
                  <Spinner />
                </div>
              )}

              {!isLoadingSlots && blockedMessage && (
                <div className="cc-blocked">{blockedMessage}</div>
              )}

              {!isLoadingSlots && !blockedMessage && !hasSlots && (
                <div className="cc-empty">
                  No slots open on this date. Try another day.
                </div>
              )}

              {!isLoadingSlots && !!reserved.length && (
                <div className="cc-reserved">
                  <div className="cc-group-label">
                    <LockIcon />
                    Reserved for new patients
                  </div>
                  <div className="cc-slot-list">{reserved.map(renderSlot)}</div>
                </div>
              )}

              {!isLoadingSlots && !!grouped.length && (
                <>
                  <div className="cc-general-label">General availability</div>
                  {grouped.map((group) => (
                    <div className="cc-slot-group" key={group.key}>
                      <div className="cc-group-label">
                        <group.Icon />
                        {group.label}
                      </div>
                      <div className="cc-slot-list">
                        {group.slots.map(renderSlot)}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="cc-foot">
          <Button variant="outlined" onClick={onClose} disabled={isBooking}>
            Cancel
          </Button>
          <Button
            onClick={handleBook}
            disabled={!selectedSlot || isBooking}
            loading={isBooking}
          >
            Confirm &amp; Book
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CreateConsultationModal;
